import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';

export interface VerifyColumn {
  agrees: boolean | null;
  course_id: string | null;
  line_item: string;
  lms_says: string | null;
}

export interface StudentOutcome {
  at?: string;
  outcome: 'delivered' | 'failed' | 'waiting';
  reason?: string;
  /** The LMS's own message for the failed post. */
  detail?: string | null;
  /** The scoreGiven the LMS accepted — a 0 here is worth saying out loud. */
  score?: number;
  student_id: number;
}

export interface VerifyPayload {
  delivery: {
    delivered: number;
    failed: number;
    sent_at: string;
    students: StudentOutcome[];
    waiting: number;
  } | null;
  link: { columns: VerifyColumn[]; linked: boolean; resource_link_id: boolean };
  /** Null while a re-send is being polled: the count is only for the Send button. */
  to_send: number | null;
  /** Zeros for missing work the normal sync owes; never sent from the dashboard. Null while polling. */
  zeros: number | null;
}

export interface Sent { sent_at: string; student_ids: number[] }

const POLL_MS = 4000;
// Past this, a student still waiting was skipped without a record (no submission, say) — stop polling.
export const GIVE_UP_MS = 3 * 60 * 1000;

const useVerify = (assignmentId: string, sent: Sent | null) => {
  const queryClient = useQueryClient();

  const verify = useQuery({
    queryFn: async (): Promise<VerifyPayload> => {
      const { data } = await axios.get(Routes.plato_api_sysadmin_grade_sync_verify_path(assignmentId), {
        params: sent == null ? {} : { since: sent.sent_at, student_ids: sent.student_ids },
      });

      return data.data;
    },
    queryKey: ['gradeSyncVerify', assignmentId, sent?.sent_at],
    refetchInterval: (data) => {
      if (sent == null || (data?.delivery?.waiting ?? 1) === 0) return false;

      return Date.now() - new Date(sent.sent_at).getTime() < GIVE_UP_MS ? POLL_MS : false;
    },
  });

  const done = verify.data?.delivery != null && verify.data.delivery.waiting === 0;

  // Once nothing is waiting, the rest of the page's numbers are stale — reload them with the result.
  useEffect(() => {
    if (!done) return;

    queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', assignmentId] });
    queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignments'] });
  }, [assignmentId, done, queryClient]);

  const redrive = useMutation({
    mutationFn: async (): Promise<Sent> => {
      const { data } = await axios.post(Routes.plato_api_sysadmin_grade_sync_redrive_path(assignmentId));

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncRepairs'] });
    },
  });

  return { redrive, verify };
};

export default useVerify;
